import React, { useState, useEffect } from 'react';

import { motion, AnimatePresence } from 'motion/react';
import { 
  FileText, 
  ShieldCheck, 
  DollarSign, 
  Database, 
  Globe, 
  Lock, 
  Cpu, 
  ShoppingCart, 
  Award, 
  Scale, 
  Clock,
  PhoneCall,
  Maximize2,
  Minimize2,
  X
} from 'lucide-react';

interface TermsAndConditionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SectionItem {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

export const TermsAndConditionsModal: React.FC<TermsAndConditionsModalProps> = ({ isOpen, onClose }) => {
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Close or exit full screen on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullScreen) {
          setIsFullScreen(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isFullScreen, onClose]);

  const sections: SectionItem[] = [
    {
      id: "payment-fees",
      title: "Payment, Basic Hourly Service Rates, Remote Support, Hardware Drop-Off & Network Engineering",
      icon: <DollarSign className="w-4 h-4 text-brand-400" />,
      content: (
        <div className="space-y-4">
          <p className="leading-relaxed">
            Payment is due upon receipt of services or delivery of equipment. We accept cash, checks (please make checks payable to <strong className="text-white">Randon Brown, LLC</strong>), and major credit or debit cards processed securely via <strong className="text-white">Zoho Payments</strong>. All credit and debit card transactions processed through Zoho Payments are subject to a card processing service charge. Returned checks are subject to a $25 service charge plus any bank-assessed returned-item fees, and the outstanding balance must be settled immediately via cash, credit card, or electronic payment. Diagnostic fees cover the time and labor required to test and evaluate equipment; they apply regardless of whether the client elects to proceed with further repairs. Repair work proceeds only upon client authorization (verbal, electronic, or written).
          </p>

          {/* Official Services & Pricing Matrix Schedule */}
          <div className="pt-2 space-y-3">
            <div className="flex items-center justify-between border-b border-neutral-900 pb-2">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-brand-400">
                Official Hourly Rates &amp; Pricing Matrix Schedule
              </span>
              <span className="text-[11px] font-mono text-slate-500">
                Standard Rates
              </span>
            </div>

            {/* Pricing Matrix Table */}
            <div className="overflow-x-auto rounded-xl border border-neutral-800 bg-neutral-950/60 print:border-neutral-300">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-neutral-800 bg-neutral-900/80 text-slate-400 font-mono text-[11px]">
                    <th className="py-2.5 px-3 font-semibold">Service Tier</th>
                    <th className="py-2.5 px-3 font-semibold">Rate</th>
                    <th className="py-2.5 px-3 font-semibold">Billing Minimum &amp; Increments</th>
                    <th className="py-2.5 px-3 font-semibold">Coverage &amp; Travel Policy</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-900 text-slate-300">
                  <tr className="hover:bg-neutral-900/40">
                    <td className="py-2.5 px-3 font-medium text-white">
                      <div className="font-bold">Remote Support</div>
                      <div className="text-[11px] text-slate-400">Screen Share Diagnostics</div>
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-brand-400 whitespace-nowrap">$37.50 / 30m</td>
                    <td className="py-2.5 px-3">30-min minimum ($37.50), then $37.50 per 30m thereafter (rounded up)</td>
                    <td className="py-2.5 px-3">Anywhere with internet — zero travel fee</td>
                  </tr>
                  <tr className="hover:bg-neutral-900/40">
                    <td className="py-2.5 px-3 font-medium text-white">
                      <div className="font-bold">In-Shop / Bench Service</div>
                      <div className="text-[11px] text-slate-400">Device Drop-Off</div>
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-brand-400 whitespace-nowrap">$75 / hour</td>
                    <td className="py-2.5 px-3">1-hr minimum ($75), then $37.50 per 30m thereafter (rounded up)</td>
                    <td className="py-2.5 px-3">Open to all clients dropping off hardware — zero travel fee</td>
                  </tr>
                  <tr className="hover:bg-neutral-900/40">
                    <td className="py-2.5 px-3 font-medium text-white">
                      <div className="font-bold">Hometown Rate</div>
                      <div className="text-[11px] text-slate-400">Licking &amp; 10-Mile Radius</div>
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-brand-400 whitespace-nowrap">$75 / hour</td>
                    <td className="py-2.5 px-3">1-hr minimum ($75), then $37.50 per 30m thereafter (rounded up)</td>
                    <td className="py-2.5 px-3">Licking city limits (65542) &amp; within 10-mile radius (travel baked in)</td>
                  </tr>
                  <tr className="hover:bg-neutral-900/40">
                    <td className="py-2.5 px-3 font-medium text-white">
                      <div className="font-bold">Texas County On-Site</div>
                      <div className="text-[11px] text-slate-400">County Route</div>
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-brand-400 whitespace-nowrap">$100 / hour</td>
                    <td className="py-2.5 px-3">1-hr minimum ($100), then $50.00 per 30m thereafter (rounded up)</td>
                    <td className="py-2.5 px-3">Houston, Cabool, Raymondville, Summersville, Plato &amp; townships (travel baked in)</td>
                  </tr>
                  <tr className="hover:bg-neutral-900/40">
                    <td className="py-2.5 px-3 font-medium text-white">
                      <div className="font-bold">Regional Route On-Site</div>
                      <div className="text-[11px] text-slate-400">Regional Highway Travel</div>
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-brand-400 whitespace-nowrap">$150 / hour</td>
                    <td className="py-2.5 px-3">1-hr minimum ($150), then $75.00 per 30m thereafter (rounded up)</td>
                    <td className="py-2.5 px-3">Rolla, Salem, Mountain Grove, Willow Springs, Waynesville (travel baked in)</td>
                  </tr>
                  <tr className="hover:bg-neutral-900/40">
                    <td className="py-2.5 px-3 font-medium text-white">
                      <div className="font-bold">Network Engineering</div>
                      <div className="text-[11px] text-slate-400">Infrastructure &amp; Enterprise</div>
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-brand-400 whitespace-nowrap">$200 / hour</td>
                    <td className="py-2.5 px-3">1-hr minimum ($200), then $100.00 per 30m thereafter (rounded up)</td>
                    <td className="py-2.5 px-3">Commercial servers, VLANs, rack wiring, Starlink &amp; NVR cameras</td>
                  </tr>
                  <tr className="hover:bg-neutral-900/40">
                    <td className="py-2.5 px-3 font-medium text-white">
                      <div className="font-bold">Website Builds</div>
                      <div className="text-[11px] text-slate-400">Custom Web Development</div>
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-brand-400 whitespace-nowrap">From $2,000</td>
                    <td className="py-2.5 px-3">Project Scope (50% kickoff deposit, 50% upon final live launch approval)</td>
                    <td className="py-2.5 px-3">100% client direct ownership of domain registrars &amp; hosting accounts</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Travel, Minimum & Payment Summary Notice */}
            <div className="p-3 rounded-xl bg-neutral-900/60 border border-neutral-800 text-[11px] text-slate-400 leading-relaxed space-y-1">
              <p>
                <strong className="text-white font-mono">Travel, Incremental Billing &amp; Payment Methods:</strong> All on-site hourly rates bundle normal transit and round-trip travel time directly into the quoted rate with zero surprise mileage fees. Standard service calls carry a 1-hour minimum charge (or 30-min minimum for remote desktop), and are billed in 30-minute increments thereafter, rounded up to the nearest 30-minute block. We accept cash, check, and major credit/debit cards via Zoho Payments (credit card payments are subject to a card processing service charge).
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "estimates-sticker-shock",
      title: "Job Time Estimates & 'No Sticker Shock' Guarantee",
      icon: <Clock className="w-4 h-4 text-brand-400" />,
      content: `To ensure complete transparency and eliminate unexpected surprises, clients are provided with upfront time estimates based on typical repair durations prior to commencing work. All estimates reflect our 1-hour minimum and billing in 30-minute increments. In all cases, if service or diagnostics reveal that a job will require more time than the initial estimate or pre-authorized budget, work is paused immediately and the client will be contacted for direct authorization before any additional billable time is incurred. You will never receive an unexpected bill or unauthorized overtime charges.`
    },
    {
      id: "phone-consultations",
      title: "Phone Consultations and Verbal Authorizations",
      icon: <PhoneCall className="w-4 h-4 text-brand-400" />,
      content: `Communications conducted via telephone, voice call, or remote conferencing regarding project scope, troubleshooting, or repairs are subject to our standard hourly billing rates and 30-minute minimum increments. Verbal authorizations or approvals provided by the client during phone calls to proceed with additional diagnostics, extended labor, or scope changes are deemed binding under the terms of this agreement and will be documented for client records.`
    },
    {
      id: "data-backup",
      title: "Data & Backup Responsibility",
      icon: <Database className="w-4 h-4 text-brand-400" />,
      content: (
        <div className="space-y-3">
          <p>
            We take data backups seriously and maintain redundant backups on various platforms during active development. However, Randon Brown, LLC does not guarantee 100% protection against unforeseen data loss, corruption, or third-party service failures.
          </p>
          <p>
            Prior to any major diagnostic, update, or system migration, clients are strictly required to maintain current, verified backups of all personal and business data. Randon Brown, LLC shall not be held liable for any loss of data, revenue, or business interruption arising from the client’s failure to maintain independent backups or from actions taken by third-party hosting providers and plugins.
          </p>
          <div className="pt-1 space-y-1">
            <strong className="text-white font-semibold block">Websites</strong>
            <p>
              Upon project completion, a complete copy of the website/data will be delivered to the client. Once delivered and signed off, ongoing maintenance, security updates, and daily data backup responsibilities transfer entirely to the client, unless a separate ongoing maintenance agreement is in place at the hourly rate specified.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "web-cloud",
      title: "Web, Domain & Hosting Services",
      icon: <Globe className="w-4 h-4 text-brand-400" />,
      content: `Custom website development projects start at $2,000 (billed as a 50% deposit upon kickoff, and the remaining 50% final payment upon completion prior to live launch), which includes custom responsive layout design, SEO optimization, and standard lead routing forms. For domain purchases, we recommend Cloudflare. On the hosting side, we take care of all website edits at our hourly rate. If you need to edit your own site, additional charges apply based upon need. On hosting management: To ensure that you retain ownership of your files, we require that your site is hosted on your hosting account and that we are added as a delegated administrator on your account to ensure security. Regarding contact forms: initial volume beyond 250 submissions requires a small upgrade fee to increase submission limits. Our forms are hosted on a third-party provider to ensure security, data compliance, and to ensure that you own your submission data. The client retains 100% direct ownership of their domain registrars, DNS managers, and hosting accounts.`
    },
    {
      id: "privacy-confidentiality",
      title: "Privacy & Confidentiality",
      icon: <Lock className="w-4 h-4 text-brand-400" />,
      content: `Client privacy is strictly protected. Personal information submitted through online quote requests and website onboarding forms (including names, contact details, diagnostic notes, domain preferences, and digital signatures) is encrypted and securely stored in Google Cloud Firestore and transmitted via secure transactional email (Resend API) strictly for service fulfillment. We do not sell, rent, or share personal information with third-party advertisers. Equipment data and personal files will not be browsed, inspected, or disclosed beyond what is strictly necessary to complete diagnostics, repairs, or requested data transfers.`
    },
    {
      id: "hardware-risk",
      title: "Hardware Risk & Pre-Existing Defects",
      icon: <Cpu className="w-4 h-4 text-brand-400" />,
      content: `Due to component age, normal wear, thermal degradation, or pre-existing hardware defects, components (including mechanical drives, SSDs, capacitors, and system boards) may fail unpredictably during testing or diagnostics. Randon Brown, LLC assumes no liability for pre-existing defects or secondary component failure.`
    },
    {
      id: "parts-procurement",
      title: "Hardware & Parts Procurement",
      icon: <ShoppingCart className="w-4 h-4 text-brand-400" />,
      content: `To provide transparent pricing without retail markups, Randon Brown, LLC does not retail or inventory physical replacement parts. When upgrades or repairs require hardware components (such as storage drives, memory, or system boards), Randon Brown, LLC will identify compatible parts and provide direct purchase links for the client to order or provide verbal instruction. The client is solely responsible for purchasing the hardware, managing manufacturer warranties, and handling any product returns or exchanges. Minor consumables, installation materials, and diagnostic media used during service are incorporated into standard labor rates and carry no separate warranty. Randon Brown, LLC is not liable for manufacturing defects or shipping delays, and all labor required to test, configure, or install hardware constitutes a billable service.`
    },
    {
      id: "service-acceptance",
      title: "Service Acceptance & No Ongoing Warranty",
      icon: <Award className="w-4 h-4 text-brand-400" />,
      content: `All services are rendered on an incident-by-incident basis. Client acceptance of the equipment or payment of the invoice confirms that the requested work was inspected and completed to satisfaction. No ongoing warranty, guarantee of future performance, or complimentary follow-up support is provided. Any subsequent issues, user modifications, malware reinfections, or secondary hardware failures occurring after the equipment leaves the service provider’s possession constitute a new billable service. Hardware replacement parts carry only the manufacturer's warranty.`
    },
    {
      id: "limitation-liability",
      title: "Limitation of Liability",
      icon: <Scale className="w-4 h-4 text-brand-400" />,
      content: `To the maximum extent permitted by applicable law, Randon Brown, LLC shall not be liable for any indirect, incidental, special, or consequential damages—including lost profits, lost revenue, business interruption, or loss of productivity—arising from technical services, delays, or equipment downtime. Total aggregate liability for any and all claims, damages, or losses shall not exceed the total service fee charged on the final invoice.`
    },
    {
      id: "abandoned-property",
      title: "Abandoned Property & Storage",
      icon: <Clock className="w-4 h-4 text-brand-400" />,
      content: `Equipment not picked up or scheduled for return within 30 days following written notice of completion or diagnostic assessment (via text message, email, or physical mail) will be deemed legally abandoned. Abandoned equipment may be recycled, repurposed, or sold to satisfy outstanding labor, diagnostic, and storage balances.`
    }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="printable-modal-wrapper fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-4 md:p-6 overflow-hidden">
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="printable-modal-backdrop absolute inset-0 bg-black/85 backdrop-blur-md"
            onClick={onClose}
          />

          {/* Modal Dialog Card */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.2 }}
            className={`printable-modal-card relative flex flex-col bg-[#0c0c0c] border border-neutral-800 shadow-2xl z-10 text-slate-300 transition-all duration-200 ${
              isFullScreen 
                ? 'fixed inset-0 w-full h-full max-w-none max-h-none rounded-none border-0' 
                : 'w-full max-w-4xl lg:max-w-5xl max-h-[90vh] rounded-2xl sm:rounded-3xl overflow-hidden m-2 sm:m-0'
            }`}
          >
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-neutral-900 flex justify-between items-center shrink-0 bg-neutral-950/95 sticky top-0 z-20 print-hide">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-500/10 text-brand-400 rounded-xl border border-brand-500/20">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-sm sm:text-base text-white font-display flex items-center gap-2">
                    Terms &amp; Conditions
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-brand-500/10 text-brand-400 border border-brand-500/20">
                      Operating Agreement
                    </span>
                  </h3>
                  <p className="text-[10px] sm:text-[11px] font-mono text-slate-500">Randon Brown, LLC | Technical Services Policies</p>
                </div>
              </div>

              {/* Action Buttons: Pop Out Full Screen, Close */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsFullScreen(!isFullScreen)}
                  className="px-3 py-1.5 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 active:scale-95 shadow-sm"
                  title={isFullScreen ? "Exit Full Screen" : "Pop Out to Full Screen"}
                >
                  {isFullScreen ? (
                    <>
                      <Minimize2 className="w-3.5 h-3.5 text-brand-400" />
                      <span className="hidden sm:inline text-xs font-mono">Restore</span>
                    </>
                  ) : (
                    <>
                      <Maximize2 className="w-3.5 h-3.5 text-brand-400" />
                      <span className="hidden sm:inline text-xs font-mono">Pop Out</span>
                    </>
                  )}
                </button>

                <button 
                  type="button"
                  onClick={onClose}
                  className="p-1.5 hover:bg-neutral-900 rounded-full transition-colors text-slate-400 hover:text-white cursor-pointer"
                  aria-label="Close Terms & Conditions"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Print Header (Visible ONLY when printing) */}
            <div className="hidden print:block print-header">
              <h1 className="print-header-title">Randon Brown, LLC | Terms &amp; Conditions</h1>
              <p className="print-header-sub">Operating Agreement &amp; Technical Services Policies • randonbrown.com</p>
              <p className="print-header-sub">Effective Date: October 2026 • Contact: help@randonbrown.com • (314) 944-3074 • Texas County, MO</p>
            </div>

            {/* Scrollable Modal Body */}
            <div className="printable-modal-body p-5 sm:p-7 md:p-8 overflow-y-auto custom-scrollbar flex-1 space-y-4 text-xs sm:text-sm leading-relaxed text-left">
              {/* Summary note */}
              <div className="p-4 rounded-2xl bg-brand-500/[0.08] border border-brand-500/25 flex items-start gap-3.5 print:border-gray-300">
                <FileText className="w-5 h-5 text-brand-400 shrink-0 mt-0.5 print-hide" />
                <p className="text-slate-300 text-xs sm:text-sm leading-relaxed print-text">
                  Standard legal terms governing all on-site service, diagnostics, hardware repairs, web development, and cloud consulting provided by Randon Brown, LLC. All work is conducted with transparent rates, upfront estimates, and verified backup safeguards.
                </p>
              </div>

              {/* Sections List */}
              <div className="space-y-4 pt-1">
                {sections.map((section, idx) => (
                  <div
                    key={section.id}
                    className="print-section-item p-4 sm:p-5 rounded-2xl bg-neutral-950/80 border border-neutral-900/90 hover:border-brand-500/30 transition-all space-y-2"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="p-1.5 rounded-lg bg-neutral-900 text-brand-400 border border-neutral-800 shrink-0 print-hide">
                        {section.icon}
                      </div>
                      <div>
                        <span className="text-[10px] font-mono text-brand-400 uppercase tracking-wider block font-bold">
                          Clause {idx + 1}
                        </span>
                        <h4 className="text-sm sm:text-base font-bold text-white font-display print-title">
                          {section.title}
                        </h4>
                      </div>
                    </div>
                    <div className="text-xs sm:text-sm text-slate-300 leading-relaxed pl-0 sm:pl-9 print-text">
                      {section.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 sm:p-5 bg-neutral-950/95 border-t border-neutral-900 flex items-center justify-between gap-3 shrink-0 print-hide">
              <span className="text-[11px] font-mono text-slate-500 hidden sm:inline">
                QUESTIONS ABOUT SERVICE POLICIES? <a href="mailto:help@randonbrown.com" className="text-brand-400 hover:underline">HELP@RANDONBROWN.COM</a>
              </span>
              <div className="flex items-center gap-2 ml-auto">
                <button 
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 bg-brand-500 hover:bg-brand-400 text-neutral-950 text-xs font-bold font-mono uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-md"
                >
                  Close &amp; Return
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TermsAndConditionsModal;
